import type {Options} from 'react-markdown'
import type {ComponentType} from 'react'
import {
  createOmicsCodeOverride,
  defaultOmicsCodeBlockComponents,
  OmicsPreOverride,
} from './omicsMarkdownCodeOverrides'
import {memoizeOmicsMarkdownComponents} from './omicsMarkdownMemo'
import {omicsMarkdownTableComponents} from './omicsMarkdownTable'
import type {OmicsCodeHeaderProps, OmicsSyntaxHighlighterProps} from './omicsMarkdownTypes'

export type OmicsCodeBlockOverrides = {
  SyntaxHighlighter?: ComponentType<OmicsSyntaxHighlighterProps>
  CodeHeader?: ComponentType<OmicsCodeHeaderProps>
}

export type OmicsMarkdownUserComponents = OmicsCodeBlockOverrides &
  Partial<Omit<NonNullable<Options['components']>, 'pre' | 'code' | 'SyntaxHighlighter' | 'CodeHeader'>>

/**
 * Build react-markdown components with fenced-code pipeline (PreOverride + CodeOverride behavior).
 * Without this, `SyntaxHighlighter` / `CodeHeader` keys are ignored by stock react-markdown.
 */
export function buildOmicsMarkdownComponents(
  userComponents?: OmicsMarkdownUserComponents,
): NonNullable<Options['components']> {
  const {
    SyntaxHighlighter = defaultOmicsCodeBlockComponents.SyntaxHighlighter,
    CodeHeader = defaultOmicsCodeBlockComponents.CodeHeader,
    ...componentsRest
  } = userComponents ?? {}

  const codeBlockComponents = {
    ...defaultOmicsCodeBlockComponents,
    SyntaxHighlighter,
    CodeHeader,
  }

  const CodeComponent = createOmicsCodeOverride(codeBlockComponents)

  const merged = {
    ...omicsMarkdownTableComponents,
    ...(componentsRest as Record<string, ComponentType<Record<string, unknown>> | undefined>),
    pre: OmicsPreOverride as ComponentType<Record<string, unknown>>,
    code: CodeComponent as ComponentType<Record<string, unknown>>,
  }

  return memoizeOmicsMarkdownComponents(merged) as NonNullable<Options['components']>
}

/** Exposed for tests: fenced blocks use code override pipeline, not raw pre/code. */
export const OMICS_MARKDOWN_USES_CODE_OVERRIDE = true
